import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, Loader2, ArrowUpRight } from "lucide-react";
import { resolveSite } from "../lib/siteConfig";
import { usePublicData } from "../hooks/usePublicData";
import { useLanguage } from "../context/LanguageContext";
import { API_BASE } from "../lib/env";
import { pub } from "../lib/pubThemeClasses";

export default function Contact() {
  const { settings } = usePublicData();
  const siteConfig = resolveSite(settings);
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("toast.contact.missing"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_BASE}/contact`, form);
      toast.success(t("toast.contact.ok"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.contact.fail"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <section className={`relative border-b bg-grain ${pub.borderSubtle}`}>
        <div className="absolute inset-0 grid-lines opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF4F00] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[#FF4F00]" /> {t("contact.eyebrow")}
          </div>
          <h1 className={`font-display text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight leading-[0.9] max-w-4xl ${pub.heroHeading}`}>
            {t("contact.hero.row1")}
            <br />
            <span className="text-[#FF4F00]">{t("contact.hero.bold")}</span>
            {t("contact.hero.rest")}
          </h1>
          <p className={`mt-6 text-lg max-w-2xl ${pub.muted}`}>{t("contact.hero.sub")}</p>
        </div>
      </section>

      <section className={`border-b ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className={`lg:col-span-5 space-y-px overflow-hidden rounded-sm border ${pub.gridHairline} ${pub.borderSubtle}`}>
            <ContactRow icon={Phone} label={t("contact.row.phone")} value={siteConfig.phone} href={`tel:${siteConfig.phone}`} testid="contact-phone" />
            <ContactRow icon={Mail} label={t("contact.row.email")} value={siteConfig.email} href={`mailto:${siteConfig.email}`} testid="contact-email" />
            <ContactRow icon={MessageCircle} label={t("contact.row.whatsapp")} value={siteConfig.whatsappDisplay} href={siteConfig.whatsappLink()} testid="contact-whatsapp" external />
            {siteConfig.address?.trim() ? (
              <ContactRow icon={MapPin} label={t("contact.row.address")} value={siteConfig.address.trim()} testid="contact-address" />
            ) : null}
            <div className={`bg-surface-2 p-6 border-t ${pub.borderSubtle}`}>
              <div className={`font-mono text-xs uppercase tracking-[0.15em] mb-2 ${pub.mutedStr}`}>{t("contact.hours.title")}</div>
              <div className={`font-sans text-base font-medium ${pub.heroHeading}`}>{siteConfig.workingHours}</div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7" data-testid="contact-form">
            <div className={`grid grid-cols-1 md:grid-cols-2 overflow-hidden rounded-sm border ${pub.gridHairline} ${pub.borderSubtle}`}>
              <Field label={t("contact.field.name")}>
                <input required value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="contact-name-input" className={inputCls} placeholder={t("quote.placeholder.name")} />
              </Field>
              <Field label={t("contact.field.email")}>
                <input required type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="contact-email-input" className={inputCls} placeholder={t("quote.placeholder.email")} />
              </Field>
              <Field label={t("contact.field.phone")}>
                <input value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="contact-phone-input" className={inputCls} placeholder={t("quote.placeholder.phone")} />
              </Field>
              <Field label={t("contact.field.subject")}>
                <input value={form.subject} onChange={(e) => upd("subject", e.target.value)} data-testid="contact-subject-input" className={inputCls} placeholder={t("contact.placeholder.subject")} />
              </Field>
              <Field label={t("contact.field.message")} span="md:col-span-2">
                <textarea required rows={6} value={form.message} onChange={(e) => upd("message", e.target.value)} data-testid="contact-message-input" className={inputCls + " h-auto py-3"} placeholder={t("contact.placeholder.message")} />
              </Field>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="inline-flex items-center justify-between gap-2 bg-[#FF4F00] hover:bg-[#E64700] disabled:opacity-50 text-black font-bold uppercase tracking-wider text-sm px-6 py-5 transition-all hover:-translate-y-0.5"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> {t("contact.submit.sending")}
                  </>
                ) : (
                  <>
                    {t("contact.submit.cta")} <ArrowUpRight className="w-5 h-5" />
                  </>
                )}
              </button>
              <a
                href={siteConfig.whatsappLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-button"
                className={`inline-flex items-center justify-between gap-2 font-bold uppercase tracking-wider text-sm px-6 py-5 ${pub.btnGhost}`}
              >
                {t("contact.whats.cta")} <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

const inputCls = `${pub.input} ${pub.inputFocus} rounded-sm font-sans text-sm h-12 px-4 transition-colors`;

function Field({ label, children, span = "" }) {
  return (
    <label className={`bg-surface-1 p-5 block ${span}`}>
      <span className={`${pub.labelMuted} mb-2 block`}>{label}</span>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid, external }) {
  const Wrap = href ? "a" : "div";
  return (
    <Wrap
      href={href}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      data-testid={testid}
      className="bg-surface-2 p-6 flex items-start gap-4 hover:bg-surface-3 transition-colors group"
    >
      <div className={`w-11 h-11 border text-neutral-800 grid place-items-center shrink-0 transition-colors group-hover:border-orange-400/65 dark:text-white dark:group-hover:border-orange-400/45 ${pub.borderSubtle}`}>
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-mono text-xs uppercase tracking-[0.2em] mb-1 ${pub.mutedStr}`}>{label}</div>
        <div className={`font-display text-base md:text-lg truncate ${pub.heroHeading}`}>{value}</div>
      </div>
    </Wrap>
  );
}
